import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable, { TableColumn } from "react-data-table-component";
import AdminDashboardLayout from "../../components/admin/AdminDashboardLayout";
import DashboardCard from "../../components/common/DashboardCard";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { permissionCheckerService } from "../../services/permission.checker.service";
import {
  adminRoleService,
  AdminRole,
  AdminModule,
  AdminAction,
  GetAdminRolesParams,
} from "../../services/admin.role.service";
import { errorService } from "../../services/error.service";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faSearch,
  faCircleNotch,
  faTriangleExclamation,
  faLock,
  faEye,
  faChevronLeft,
  faCheckCircle,
  faSave,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import CreateRoleModal from "../../components/admin/CreateRoleModal";
import styles from "../../styles/pages/AdminRolesPage.module.css";
import btnStyles from "../../styles/components/Button.module.css";

const AdminRolesPage: React.FC = () => {
  const navigate = useNavigate();
  const { adminData } = useAdminAuth();

  useEffect(() => {
    if (!permissionCheckerService.checkPermission(adminData, "ROLES", "READ")) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [adminData, navigate]);

  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Navigation states
  const [currentView, setCurrentView] = useState<"roles" | "modules">("roles");
  const [selectedRole, setSelectedRole] = useState<AdminRole | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Filter states
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");

  const fetchRoles = async (page: number, size: number) => {
    try {
      setLoading(true);
      setError(null);

      const params: GetAdminRolesParams = {
        page: page,
        pageSize: size,
        status: statusFilter ? parseInt(statusFilter) : undefined,
        SearchString: searchText || undefined,
      };

      const response = await adminRoleService.getRoles(params);

      if (response.statusCode === 200) {
        setRoles(response.data);
        setTotalRows(response.pagination.totalCount);
      } else {
        setError(errorService.getDisplayMessage(response));
      }
    } catch (err: any) {
      console.error("Error fetching admin roles:", err);
      setError(errorService.getDisplayMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles(1, perPage);
  }, []);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchRoles(page, perPage);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    fetchRoles(page, newPerPage);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchRoles(1, perPage);
  };

  const handleViewModules = (role: AdminRole) => {
    if (role.isSuperAdmin) return;
    setSelectedRole(role);
    setCurrentView("modules");
  };

  const showAssignedUsersAlert = (users: string[], roleName: string) => {
    const userListHtml = `
      <div style="text-align: left; font-size: 14px; color: #334155;">
        <p style="margin-bottom: 12px;">
          Primero debes retirar el rol <strong>"${roleName}"</strong> de los siguientes usuarios antes de poder eliminarlo:
        </p>
        <ul style="max-height: 180px; overflow-y: auto; background: #f8fafc; padding: 12px 24px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 13px; color: #0f172a; margin: 0;">
          ${users.map((name) => `<li style="margin-bottom: 6px;"><strong>${name}</strong></li>`).join("")}
        </ul>
      </div>
    `;

    Swal.fire({
      icon: "error",
      title: "No se puede eliminar el rol",
      html: userListHtml,
      confirmButtonText: "Entendido",
      confirmButtonColor: "#ef4444",
    });
  };

  const handleDeleteRole = async (role: AdminRole) => {
    if (role.isSuperAdmin) return;

    const result = await Swal.fire({
      title: "¿Eliminar rol?",
      text: `¿Estás seguro de que deseas eliminar el rol "${role.roleName}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef4444",
    });

    if (result.isConfirmed) {
      try {
        const response = await adminRoleService.deleteRole({ idRole: role.id });
        if (response.statusCode === 200) {
          await Swal.fire({
            icon: "success",
            title: "¡Eliminado!",
            text: "El rol ha sido eliminado correctamente.",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "var(--elPaket-primary-color, #e84d30)",
          });
          fetchRoles(currentPage, perPage);
        } else if ((response as any).assignedUsers && (response as any).assignedUsers.length > 0) {
          showAssignedUsersAlert((response as any).assignedUsers, role.roleName);
        } else {
          errorService.showErrorAlert(response, "Error al Eliminar");
        }
      } catch (err: any) {
        const responseData = err?.response?.data;
        if (responseData && responseData.assignedUsers && responseData.assignedUsers.length > 0) {
          showAssignedUsersAlert(responseData.assignedUsers, role.roleName);
        } else {
          console.error("Error deleting role:", err);
          errorService.showErrorAlert(err, "Error de Conexión");
        }
      }
    }
  };

  const handleBackToRoles = () => {
    setCurrentView("roles");
    setSelectedRole(null);
  };

  const handleTogglePermission = (moduleId: number, actionId: number) => {
    if (!selectedRole || selectedRole.isSuperAdmin) return;

    const updatedModules = selectedRole.modules.map((mod) => {
      if (mod.id === moduleId) {
        return {
          ...mod,
          actions: mod.actions.map((act) => {
            if (act.id === actionId) {
              return { ...act, hasPermission: !act.hasPermission };
            }
            return act;
          }),
        };
      }
      return mod;
    });

    setSelectedRole({
      ...selectedRole,
      modules: updatedModules,
    });
  };

  const handleSaveRole = async () => {
    if (!selectedRole || selectedRole.isSuperAdmin) return;

    const result = await Swal.fire({
      title: "¿Guardar cambios?",
      text: `Se actualizarán los permisos para el rol "${selectedRole.roleName}".`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, guardar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "var(--elPaket-primary-color, #e84d30)",
    });

    if (result.isConfirmed) {
      try {
        // Recolectar todos los IDs de acciones que tienen permiso
        const idActions: number[] = [];
        selectedRole.modules.forEach((mod) => {
          mod.actions.forEach((act) => {
            if (act.hasPermission) {
              idActions.push(act.id);
            }
          });
        });

        const data = {
          idRole: selectedRole.id,
          idActions: idActions,
        };

        const response = await adminRoleService.updateRoleActions(data);

        if (response.statusCode === 200) {
          await Swal.fire({
            icon: "success",
            title: "¡Guardado!",
            text: "Los permisos han sido actualizados correctamente.",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "var(--elPaket-primary-color, #e84d30)",
          });

          // Volver a la lista de roles
          handleBackToRoles();

          // Refrescar la lista principal para tener los datos actualizados
          // (Se mantiene en currentPage gracias a la llamada actual)
          fetchRoles(currentPage, perPage);
        } else {
          errorService.showErrorAlert(response, "Error al Guardar");
        }
      } catch (err: any) {
        console.error("Error saving role permissions:", err);
        errorService.showErrorAlert(err, "Error de Conexión");
      }
    }
  };

  const columns: TableColumn<AdminRole>[] = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      cell: (row) => (
        <div className={styles.orderNumber}>
          {row.id}
        </div>
      ),
      width: "100px",
    },
    {
      name: "Nombre del Rol",
      selector: (row) => row.roleName,
      sortable: true,
      cell: (row) => (
        row.isSuperAdmin ? (
          <span
            style={{
              backgroundColor: "#7c3aed",
              color: "#ffffff",
              fontSize: "12px",
              fontWeight: "700",
              padding: "4px 10px",
              borderRadius: "12px",
              display: "inline-block",
              letterSpacing: "0.3px",
            }}
          >
            {row.roleName}
          </span>
        ) : (
          <div style={{ fontWeight: "600", color: "#1e293b" }}>
            {row.roleName}
          </div>
        )
      ),
    },
    {
      name: "Estado",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => (
        <span
          className={`${styles.statusBadge} ${row.status === 1 ? styles.statusActive : styles.statusInactive}`}
        >
          {row.status === 1 ? "Activo" : "Inactivo"}
        </span>
      ),
      width: "120px",
    },
    {
      name: "Acciones",
      cell: (row) => (
        <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
          {!row.isSuperAdmin && permissionCheckerService.checkPermission(adminData, "ROLES", "SHOW MODULES") && (
            <button
              className={styles.btnAction}
              onClick={() => handleViewModules(row)}
              title="Ver Módulos"
            >
              <FontAwesomeIcon icon={faEye} />
            </button>
          )}
          {!row.isSuperAdmin && permissionCheckerService.checkPermission(adminData, "ROLES", "DELETE") && (
            <button
              className={styles.btnAction}
              onClick={() => handleDeleteRole(row)}
              title="Eliminar Rol"
              style={{ backgroundColor: "#ef4444", color: "#ffffff" }}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          )}
        </div>
      ),
      width: "150px",
      center: true,
    },
  ];

  const ActionDetailView: React.FC<{ data: AdminModule }> = ({ data }) => {
    const isReadOnly = selectedRole?.isSuperAdmin || !permissionCheckerService.checkPermission(adminData, "ROLES", "UPDATE ACTIONS");
    return (
      <div className={styles.actionsExpansionContainer}>
        <div className={styles.actionsGrid}>
          {data.actions.map((action: AdminAction) => (
            <div
              key={action.id}
              className={`${styles.actionCheckItem} ${isReadOnly ? styles.disabled : ""}`}
              onClick={() => {
                if (!isReadOnly) {
                  handleTogglePermission(data.id, action.id);
                }
              }}
            >
              <div
                className={`${styles.customCheckbox} ${action.hasPermission || selectedRole?.isSuperAdmin ? styles.checked : ""}`}
              >
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className={styles.checkIcon}
                />
              </div>
              <span
                className={
                  action.hasPermission || selectedRole?.isSuperAdmin
                    ? styles.actionTextActive
                    : styles.actionTextInactive
                }
              >
                {action.action}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const moduleColumns: TableColumn<AdminModule>[] = [
    {
      name: "Módulo",
      selector: (row) => row.moduleName,
      sortable: true,
      cell: (row) => (
        <span className={styles.moduleNameText}>{row.moduleName}</span>
      ),
    },
    {
      name: "Estado de Permisos",
      cell: (row) => {
        const count = row.actions.filter(
          (a: AdminAction) => a.hasPermission,
        ).length;
        const total = row.actions.length;
        return (
          <span className={styles.permissionBadge}>
            {count} de {total} asignados
          </span>
        );
      },
    },
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "var(--elPaket-primary-color)",
        color: "white",
        minHeight: "48px",
      },
    },
    headCells: {
      style: {
        color: "var(--brand-black)",
        fontWeight: "700",
        fontSize: "12px",
        textTransform: "uppercase" as any,
        letterSpacing: "0.5px",
      },
    },
    rows: {
      style: {
        minHeight: "70px",
        "&:hover": {
          backgroundColor: "#f8fafc !important",
        },
      },
    },
  };

  return (
    <AdminDashboardLayout>
      <div className={styles.adminRolesContainer}>
        <DashboardCard
          icon={faShieldAlt}
          title="Gestión de Roles"
          description="Administra los roles del sistema y sus configuraciones de acceso."
          actions={
            permissionCheckerService.checkPermission(adminData, "ROLES", "CREATE") && (
              <button
                className={btnStyles.primary}
                onClick={() => setShowCreateModal(true)}
              >
                <FontAwesomeIcon icon={faPlus} /> Crear Nuevo Rol
              </button>
            )
          }
        />

        <div className={styles.tableWrapper}>
          <div
            className={
              currentView === "roles" ? styles.viewVisible : styles.viewHidden
            }
          >
            <form className={styles.tableActions} onSubmit={handleSearch}>
              <div className={styles.filterSection}>
                <div className={styles.filterGroup}>
                  <label>Estado</label>
                  <select
                    className={styles.filterSelect}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="1">Activos</option>
                    <option value="0">Inactivos</option>
                  </select>
                </div>
              </div>

              <div className={styles.searchRow}>
                <div className={styles.searchWrapper}>
                  <FontAwesomeIcon
                    icon={faSearch}
                    className={styles.searchIcon}
                  />
                  <input
                    type="text"
                    placeholder="Buscar por nombre de rol..."
                    className={styles.searchInput}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
                <button type="submit" className={btnStyles.primary}>
                  <FontAwesomeIcon icon={faSearch} /> Buscar
                </button>
              </div>
            </form>

            {error ? (
              <div className={styles.emptyContainer}>
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  size="3x"
                  style={{ color: "#ef4444", marginBottom: "16px" }}
                />
                <h3>Error de Carga</h3>
                <p>{error}</p>
                <button
                  className={btnStyles.primary}
                  style={{ margin: "20px auto" }}
                  onClick={() => fetchRoles(currentPage, perPage)}
                >
                  Reintentar
                </button>
              </div>
            ) : roles.length === 0 && !loading ? (
              <div className={styles.emptyContainer}>
                <FontAwesomeIcon
                  icon={faLock}
                  size="3x"
                  style={{ marginBottom: "16px", opacity: 0.5 }}
                />
                <p>No se encontraron roles con los criterios de búsqueda.</p>
              </div>
            ) : (
              <div className={styles.innerTableWrapper}>
                <DataTable
                  columns={columns}
                  data={roles}
                  pagination
                  paginationServer
                  paginationTotalRows={totalRows}
                  paginationDefaultPage={currentPage}
                  onChangeRowsPerPage={handlePerRowsChange}
                  onChangePage={handlePageChange}
                  progressPending={loading}
                  progressComponent={
                    <div className={styles.loadingContainer}>
                      <FontAwesomeIcon
                        icon={faCircleNotch}
                        spin
                        size="3x"
                        className={styles.loadingIcon}
                      />
                      <p>Cargando lista de roles...</p>
                    </div>
                  }
                  customStyles={customStyles}
                  responsive
                  highlightOnHover
                  noDataComponent={null}
                />
              </div>
            )}
          </div>

          <div
            className={
              currentView === "modules" ? styles.viewVisible : styles.viewHidden
            }
          >
            <div className={styles.modulesViewContainer}>
              <div className={styles.viewHeader}>
                <button className={styles.btnBack} onClick={handleBackToRoles}>
                  <FontAwesomeIcon icon={faChevronLeft} /> Volver a Roles
                </button>
                <div className={styles.viewTitleGroup}>
                  <div className={styles.viewTitle}>
                    <FontAwesomeIcon icon={faShieldAlt} />
                    <h3>
                      Configuración de Módulos:{" "}
                      <span>{selectedRole?.roleName}</span>
                    </h3>
                  </div>
                  {!selectedRole?.isSuperAdmin && permissionCheckerService.checkPermission(adminData, "ROLES", "UPDATE ACTIONS") && (
                    <button className={btnStyles.primary} onClick={handleSaveRole}>
                      <FontAwesomeIcon icon={faSave} /> Guardar Cambios
                    </button>
                  )}
                </div>
              </div>

              {selectedRole?.isSuperAdmin && (
                <div style={{
                  backgroundColor: "#f3e8ff",
                  borderLeft: "4px solid #7c3aed",
                  color: "#6b21a8",
                  padding: "12px 16px",
                  borderRadius: "6px",
                  marginBottom: "16px",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px"
                }}>
                  <FontAwesomeIcon icon={faLock} />
                  <span>Este rol es <strong>Super Admin</strong>. Cuenta con pase libre por todos los módulos del sistema y sus permisos no son modificables.</span>
                </div>
              )}

              <div className={styles.innerTableWrapper}>
                <DataTable
                  columns={moduleColumns}
                  data={selectedRole?.modules || []}
                  expandableRows
                  expandableRowsComponent={ActionDetailView}
                  customStyles={{
                    ...customStyles,
                    headRow: {
                      style: {
                        backgroundColor: "#f1f5f9",
                        color: "#475569",
                        minHeight: "45px",
                      },
                    },
                    headCells: {
                      style: {
                        color: "var(--brand-black)",
                        fontWeight: "700",
                      },
                    },
                  }}
                  responsive
                  highlightOnHover
                  noDataComponent={
                    <p className={styles.noModulesText}>
                      No hay módulos asignados a este rol.
                    </p>
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <CreateRoleModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSuccess={() => fetchRoles(1, perPage)}
      />
    </AdminDashboardLayout>
  );
};

export default AdminRolesPage;
